import {ChangeDetectorRef, Component, effect, Inject, OnDestroy, OnInit, signal} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router, RouterLink} from '@angular/router';
import {Subscription} from 'rxjs';
import {SessionStoreDataService} from '../../../projects/products-lib/src/lib/services/session-store-data.service';
import {ProductDataService} from '../../../projects/products-lib/src/lib/services/product-data.service';
import {DigitalLicenceService} from '../../services/digital-licence.service';
import {CgpAccountDigitalLicenceDto} from '../models/cgp-account-digital-licence.dto';
import {DigitalRevisionMenuItem} from '../../../projects/products-lib/src/lib/interfaces/digital-revision-menu-item';
import {ENV_TOKEN, EnvInterface} from 'env-config';
import {ExternalRedirect} from '../../../projects/auth/src/lib/services/external-redirect.service';
@Component({
  selector: 'app-landing-page',
  imports: [
    RouterLink,
  ],
  templateUrl: './landing-page.html',
  styleUrl: './landing-page.sass',
})
export class LandingPage implements OnInit, OnDestroy {
  private routerSub: Subscription | undefined;

  digitalLicences = signal<CgpAccountDigitalLicenceDto[]>([]);
  isLoadingLicences = signal(false);
  errorMessage = signal<string | null>(null);

  constructor(
    @Inject(ENV_TOKEN)
    protected readonly environment: EnvInterface,
    private router: Router,
    private route: ActivatedRoute,
    public sessionStore: SessionStoreDataService,
    public digitalLicenceService: DigitalLicenceService,
    private productDataService: ProductDataService,
    protected externalRedirect: ExternalRedirect,
    private changeDetectorRef: ChangeDetectorRef,
  ) {
    // Load cached licences from session storage if available
    let cachedLicences = this.sessionStore.getStoredData('digitalLicences');
    if (Array.isArray(cachedLicences) && cachedLicences.length > 0) {
      this.digitalLicences.set(cachedLicences);
    } else {
      effect(() => {
        if (this.sessionStore.isLoggedInSignal()) {
          this.fetchDigitalLicences();
        } else {
          this.digitalLicences.set([]);
          this.sessionStore.clearStoredData('digitalLicences');
        }
      });
    }
  }

  ngOnInit() {
    this.clearOrphanedSessionStorageProductData();

    // Fetch on initial load/refresh of '/'
    if (this.sessionStore.isLoggedInSignal()) {
      this.fetchDigitalLicences().then(() => this.changeDetectorRef.detectChanges());
    }

    // Subscribe to router events to re-fetch when navigating back to '/'
    this.routerSub = this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd && (event.urlAfterRedirects === '/' || event.url === '/')) {
        this.fetchDigitalLicences()
          .then(() => {
            this.changeDetectorRef.detectChanges();
          });
        this.clearOrphanedSessionStorageProductData();
      }
    });
    // Check for 'Switch' button and set cookie if needed
    this.checkAndSetCgpDigitalRevisionCookie([]); // Pass an empty array or fetch menu items as needed
  }

  ngOnDestroy(): void {
    this.routerSub?.unsubscribe();
  }

  private checkAndSetCgpDigitalRevisionCookie(menuItems: DigitalRevisionMenuItem[]) {
    // Only run in browser
    if (typeof window === 'undefined' || typeof document === 'undefined') return;
    // Find the first menu item with IsSwitcher true
    const switcherItem = Array.isArray(menuItems) ? menuItems.find(item => item.IsSwitcher) : undefined;
    if (!switcherItem || !switcherItem.SwitcherCookieName) return;
    const cookieName = switcherItem.SwitcherCookieName;
    // Check if cookie already exists
    if (document.cookie.split(';').some((item) => item.trim().startsWith(cookieName + '='))) {
      return;
    }
    // Set cookie for 1 day
    const expires = new Date(Date.now() + 86400000).toUTCString();
    let cookieStr = `${cookieName}=Higher; path=/; expires=${expires}; secure`;
    if (location.hostname.endsWith('cgpbooks.co.uk')) {
      cookieStr += '; domain=.cgpbooks.co.uk';
    }
    document.cookie = cookieStr;
  }

  // private async delay(ms: number) {
  //   return new Promise(resolve => setTimeout(resolve, ms));
  // }

  async fetchDigitalLicences() {
    this.isLoadingLicences.set(true);
    this.errorMessage.set(null);
    const userData = this.sessionStore.getDecodedTokenUser();
    if (!userData && !userData?.externalUserId) {
      console.error('User data or externalUserId not found in session store:', userData);
      this.errorMessage.set('User externalUserId not found.');
      this.isLoadingLicences.set(false);
      await this.tryRefreshLicences(userData);
      return;
    }
    await this.tryRefreshLicences(userData);
  }

  async tryRefreshLicences(userData: any) {
    try {
      const response = await this.digitalLicenceService.getDigitalProductLicenses(userData.userGuid);
      let licences: CgpAccountDigitalLicenceDto[] = [];
      if (Array.isArray(response)) {
        licences = response as CgpAccountDigitalLicenceDto[];
      } else if (Array.isArray(response?.data)) {
        licences = response.data as CgpAccountDigitalLicenceDto[];
      }
      this.digitalLicences.set(licences);
      this.sessionStore.setStoredData('digitalLicences', licences);
      //console.log('Fetched digital Licences ', licences);
    } catch (err: any) {
      //console.log('Catch error: ', err);
      this.errorMessage.set('Failed to fetch digital licences.');
    } finally {
      //console.log('Finished fetching licences, setting isLoadingLicences to false');
      this.isLoadingLicences.set(false);
    }
  }

  /** The code whose RevisionHub the licence opens. The cover image keeps the licence's own code. */
  protected hubCode(licence: CgpAccountDigitalLicenceDto): string {
    return this.productDataService.resolveHubCode(licence.rootSkuNumber);
  }

  protected onImgError(event: Event): void {
    const img = event.target as HTMLImageElement;
    img.src = '/assets/images/products/comingsoon-cover.jpg';
    img.onerror = null; // prevent infinite loop if fallback also fails
  }


  toggleCgpDigitalRevision() {
    if (typeof document === 'undefined') return;
    const match = document.cookie.match(/(?:^|; )CgpDigitalRevision=([^;]*)/);
    const value = match ? decodeURIComponent(match[1]) : 'Higher';
    const newValue = value === 'Higher' ? 'Foundation' : 'Higher';
    const expires = new Date(Date.now() + 86400000).toUTCString();
    let cookieStr = `CgpDigitalRevision=${newValue}; path=/; expires=${expires}; secure`;
    if (location.hostname.endsWith('cgpbooks.co.uk')) {
      cookieStr += '; domain=.cgpbooks.co.uk';
    }
    document.cookie = cookieStr;
    // Optionally, reload or trigger UI update if needed
    window.location.reload();
  }

  private clearOrphanedSessionStorageProductData(): void {
    if (typeof sessionStorage === 'undefined') return;
    const keysToRemove: string[] = [];
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && (key.includes('-ProductData') || key.includes('CalculatedProductData') || key.includes('---Combined'))) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => sessionStorage.removeItem(key));
  }
}
